import { EFeatureFlags } from 'constants/featureFlags'
import { PlayProfileRole } from 'constants/play-profile'
import { AuthRoleNameEnum } from 'constants/user'
import { adminSidebarItems, SidebarItem, sidebarItems } from './constants'

// Which navigation items a viewer may see, in one place.
//
// The desktop sidebar and the mobile bottom navigation have to agree: they are the
// same navigation on two screen sizes, and a page that appears in one and not the
// other reads as a bug. They had drifted — the sidebar filters by play-profile type
// on the play routes while the bottom bar filtered by org role — so a coach viewing
// their player profile got one menu on the left and a different one along the
// bottom.

export interface VisibleItemsInput {
  /** Player-only viewer: gets the flat set, as the sidebar does. */
  isBasicUser: boolean
  /** The active play profile, when on a play route. Undefined on org routes. */
  activeProfileType?: PlayProfileRole
  /** Max org role across the viewer's teams, already resolved to a name. */
  roleName?: AuthRoleNameEnum | null
  isWorkPlansEnabled: boolean
}

/**
 * The set a viewer starts from.
 *
 * A play profile always reads the flat set: the grouped admin set is the org
 * management chrome, and a coach looking at their own play surface is not
 * managing the org.
 */
export const sidebarSetFor = ({ isBasicUser, activeProfileType }: VisibleItemsInput): SidebarItem[] =>
  activeProfileType || isBasicUser ? sidebarItems : adminSidebarItems

/**
 * Whether an item survives the play-profile guard.
 *
 * Off the play routes there is no profile in context and the guard does not
 * apply. On them an item must name the active type — an item that names none is
 * org-only by convention and stays hidden.
 */
export const passesProfileTypeGuard = (item: SidebarItem, activeProfileType?: PlayProfileRole): boolean =>
  !activeProfileType || !!item.allowedProfileTypes?.includes(activeProfileType)

/** Whether an item survives the org-role guard. No `allowedRoles` = everyone. */
export const passesRoleGuard = (item: SidebarItem, roleName?: AuthRoleNameEnum | null): boolean =>
  !item.allowedRoles || (!!roleName && item.allowedRoles.includes(roleName))

/**
 * Both guards plus the feature flag, in the order the sidebar applies them.
 *
 * The profile guard replaces the role guard rather than stacking with it: on a
 * play route the question is which profile is active, and the sidebar has never
 * applied `allowedRoles` there.
 */
export const isItemVisible = (item: SidebarItem, input: VisibleItemsInput): boolean => {
  if (item.featureFlag === EFeatureFlags.WORK_PLANS && !input.isWorkPlansEnabled) return false
  return input.activeProfileType
    ? passesProfileTypeGuard(item, input.activeProfileType)
    : passesRoleGuard(item, input.roleName)
}

/**
 * Flattens grouped entries (Content / Team & Group / Operations) into plain rows.
 *
 * The bottom bar has no room for group headers, so a group contributes its
 * children and nothing else. Groups are recognised by their placeholder path.
 */
export const flattenSidebarItems = (items: SidebarItem[]): SidebarItem[] =>
  items.flatMap(item => (item.path === '/' && item.children?.length ? item.children : [item]))

/**
 * The flat, filtered list behind the mobile bottom navigation.
 *
 * Disabled entries are dropped rather than greyed out: the sidebar can afford to
 * show a page that does not exist yet, a five-slot bar cannot.
 */
export const visibleFlatItems = (input: VisibleItemsInput): SidebarItem[] =>
  flattenSidebarItems(sidebarSetFor(input)).filter(
    item => !item.isDisabled && item.path !== '/' && isItemVisible(item, input)
  )
